import React, { useState, useMemo } from 'react';
import {
  Search,
  ChevronDown,
  Eye,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { Select } from '../../components/ui/Select';
import { useModal } from '../../hooks/useModal';
import RaiseDisputeModal from '../../components/RaiseDisputeModal';
import { Input } from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';

interface DiscrepancyRow {
  id: string;
  date: string;
  productName: string;
  orderId: string;
  sku: string;
  qty: number;
  awb: string;
  courier: string;
  crn: string;
  mentionedDeadWeight: number;
  mentionedVolumetricWeight: number;
  mentionedSlab: string;
  chargedDeadWeight: number;
  chargedVolumetricWeight: number;
  chargedSlab: string;
  excessWeight: number;
  excessCharges: number;
  status: 'new' | 'accepted' | 'raised' | 'resolved';
  courierImageUrl?: string;
  hasHistory: boolean;
  remarks: Remark[];
}

interface Remark {
  date: string;
  remarkFrom: string;
  remark: string;
}

const DISCREPANCY_DATA: DiscrepancyRow[] = [
  {
    id: '1',
    date: '12-10-2025 10:30 am',
    productName: 'Dibdabishajjid',
    orderId: 'X998i',
    sku: 'NA',
    qty: 1,
    awb: '11223344556677',
    courier: 'Ekratt 2Kg',
    crn: '46589',
    mentionedDeadWeight: 1.3,
    mentionedVolumetricWeight: 0.22,
    mentionedSlab: '2 Kg Slab',
    chargedDeadWeight: 2.3,
    chargedVolumetricWeight: 1.22,
    chargedSlab: '2 + 1 Kg Slab',
    excessWeight: 1,
    excessCharges: 140.13,
    status: 'new',
    hasHistory: false,
    remarks: [],
  },
  {
    id: '2',
    date: '11-10-2025 02:15 pm',
    productName: 'Smart Watch Pro',
    orderId: 'Y445k',
    sku: 'SWP-BLK',
    qty: 1,
    awb: '11223344556678',
    courier: 'Express 1Kg',
    crn: '53309',
    mentionedDeadWeight: 0.5,
    mentionedVolumetricWeight: 0.35,
    mentionedSlab: '1 Kg Slab',
    chargedDeadWeight: 1.2,
    chargedVolumetricWeight: 0.8,
    chargedSlab: '2 Kg Slab',
    excessWeight: 1.15,
    excessCharges: 85.5,
    status: 'accepted',
    hasHistory: true,
    remarks: [
      {
        date: '11-10-2025',
        remarkFrom: 'Parcelpicks',
        remark: 'Overweight discrepancy',
      },
      {
        date: '12-10-2025',
        remarkFrom: 'Seller',
        remark: 'Accepted the discrepancy',
      },
    ],
  },
  {
    id: '3',
    date: '10-10-2025 09:45 am',
    productName: 'Wireless Earbuds',
    orderId: 'Z789l',
    sku: 'WE-TWS',
    qty: 2,
    awb: '11223344556679',
    courier: 'Premium 3Kg',
    crn: '30328',
    mentionedDeadWeight: 0.8,
    mentionedVolumetricWeight: 0.45,
    mentionedSlab: '1 Kg Slab',
    chargedDeadWeight: 1.8,
    chargedVolumetricWeight: 1.1,
    chargedSlab: '2 + 1 Kg Slab',
    excessWeight: 1.65,
    excessCharges: 120.75,
    status: 'raised',
    hasHistory: true,
    remarks: [
      {
        date: '10-10-2025',
        remarkFrom: 'ParcelBids',
        remark: 'Weight discrepancy found',
      },
      {
        date: '11-10-2025',
        remarkFrom: 'Seller',
        remark: 'Dispute raised with images',
      },
    ],
  },
  {
    id: '4',
    date: '09-10-2025 04:20 pm',
    productName: 'Laptop Backpack',
    orderId: 'A123m',
    sku: 'LB-GRY',
    qty: 1,
    awb: '11223344556680',
    courier: 'Standard 5Kg',
    crn: '46589',
    mentionedDeadWeight: 1.2,
    mentionedVolumetricWeight: 2.5,
    mentionedSlab: '5 Kg Slab',
    chargedDeadWeight: 2.1,
    chargedVolumetricWeight: 3.8,
    chargedSlab: '5 + 2 Kg Slab',
    excessWeight: 2.2,
    excessCharges: 195.25,
    status: 'new',
    hasHistory: false,
    remarks: [],
  },
];

export default function WeightDiscrepancy() {
  const [activeTab, setActiveTab] = useState<
    'new' | 'accepted' | 'raised' | 'all'
  >('new');
  const [searchTracking, setSearchTracking] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [selectedFilter, setSelectedFilter] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<'accept' | 'raise' | ''>('');
  const { open, Modal } = useModal();

  const { user } = useAuth();
  const admin = user?.roleName === 'ADMIN';
  const GRID_COLS = admin
    ? '[grid-template-columns:80px_1fr_80px_repeat(8,1fr)]'
    : '[grid-template-columns:80px_repeat(9,1fr)]'; // Added +1 for select column

  const filteredData = useMemo(() => {
    let data = DISCREPANCY_DATA;

    // Filter by tab
    if (activeTab !== 'all') {
      data = data.filter((item) => item.status === activeTab);
    }

    // Filter by search
    if (searchTracking) {
      data = data.filter(
        (item) =>
          item.awb.includes(searchTracking) ||
          item.orderId.toLowerCase().includes(searchTracking.toLowerCase()) ||
          item.crn.includes(searchTracking)
      );
    }

    // Sort by date
    data = [...data].sort((a, b) => {
      if (sortOrder === 'newest') {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    return data;
  }, [activeTab, searchTracking, sortOrder, selectedFilter]);

  const selectedRowsData = useMemo(() => {
    return filteredData.filter((row) => selectedRows.includes(row.id));
  }, [selectedRows, filteredData]);

  const isAllSelected =
    filteredData.length > 0 && selectedRows.length === filteredData.length;
  const isIndeterminate =
    selectedRows.length > 0 && selectedRows.length < filteredData.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(filteredData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, id]);
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== id));
    }
  };

  const handleBulkAction = () => {
    if (!bulkAction || selectedRowsData.length === 0) return;

    if (bulkAction === 'accept') {
      // Handle bulk accept
      // Implement bulk accept logic here
    } else if (bulkAction === 'raise') {
      // Handle bulk raise dispute
      const combinedData = combineDiscrepancies(selectedRowsData);
      handleRaiseBulkDispute(combinedData);
    }

    // Reset after action
    setBulkAction('');
    setSelectedRows([]);
  };

  const combineDiscrepancies = (rows: DiscrepancyRow[]) => {
    if (rows.length === 1) {
      return rows[0];
    }

    // Combine multiple discrepancies into one
    const combined = {
      ...rows[0],
      awb: rows.map((r) => r.awb).join(', '),
      mentionedDeadWeight: rows.reduce(
        (sum, row) => sum + row.mentionedDeadWeight,
        0
      ),
      mentionedVolumetricWeight: rows.reduce(
        (sum, row) => sum + row.mentionedVolumetricWeight,
        0
      ),
      chargedDeadWeight: rows.reduce(
        (sum, row) => sum + row.chargedDeadWeight,
        0
      ),
      chargedVolumetricWeight: rows.reduce(
        (sum, row) => sum + row.chargedVolumetricWeight,
        0
      ),
      excessWeight: rows.reduce((sum, row) => sum + row.excessWeight, 0),
      excessCharges: rows.reduce((sum, row) => sum + row.excessCharges, 0),
      productName: rows.map((r) => r.productName).join(', '),
      orderId: rows.map((r) => r.orderId).join(', '),
      sku:
        rows
          .map((r) => r.sku)
          .filter((sku) => sku !== 'NA')
          .join(', ') || 'Multiple',
      qty: rows.reduce((sum, row) => sum + row.qty, 0),
      // For description, mention this is a bulk dispute
      isBulkDispute: true,
      bulkCount: rows.length,
      bulkItems: rows.map((row) => ({
        awb: row.awb,
        productName: row.productName,
        excessCharges: row.excessCharges,
      })),
    };

    return combined;
  };

  const handleRaiseDispute = (row: DiscrepancyRow) => {
    open({
      Component: () => <RaiseDisputeModal discrepancy={row} isBulk={false} />,
      centered: true,
      closable: true,
      className: 'w-full h-[92vh] max-w-4xl rounded-2xl',
    });
  };

  const handleRaiseBulkDispute = (combinedData: any) => {
    open({
      Component: () => (
        <RaiseDisputeModal discrepancy={combinedData} isBulk={true} />
      ),
      centered: true,
      closable: true,
      className: 'w-full h-[92vh] max-w-4xl rounded-2xl',
    });
  };

  const handleAcceptDiscrepancy = (id: string) => {
    // Implement accept functionality
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'raised':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <AlertCircle size={16} />;
      case 'accepted':
        return <CheckCircle size={16} />;
      case 'raised':
        return <Clock size={16} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 lg:px-0 py-4 lg:py-6">
      <div className="mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-text">
            Weight Discrepancy
          </h1>
          <p className="text-text1 mt-2">
            Manage pending weight discrepancies, track disputes, and access your
            full history easily
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center w-full">
          <div className="flex flex-1 gap-4 min-w-[200px] max-w-xl">
            <div className="w-full">
              <Input
                placeholder="Search by Tracking number/CRN"
                value={searchTracking}
                onChange={(e) => setSearchTracking(e.target.value)}
                startEnhancer={<Search size={18} className="text-teal" />}
              />
            </div>
            {(searchTracking || selectedDate || sortOrder !== 'newest' || selectedFilter) && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTracking('');
                  setSelectedDate('');
                  setSortOrder('newest');
                  setSelectedFilter('');
                }}
                className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
              >
                Reset Filters
              </Button>
            )}
          </div>

          <Select
            className="w-32"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            placeholder="Date"
            options={[
              { label: 'Date', value: '' },
              { label: 'Today', value: 'today' },
              { label: 'This Week', value: 'week' },
              { label: 'This Month', value: 'month' },
            ]}
          />

          <Select
            className="w-28"
            value={sortOrder}
            onChange={(e) =>
              setSortOrder(e.target.value as 'newest' | 'oldest')
            }
            placeholder="Newest"
            options={[
              { label: 'Newest', value: 'newest' },
              { label: 'Oldest', value: 'oldest' },
            ]}
          />

          <Select
            className="w-32"
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            placeholder="Select"
            options={[
              { label: 'Select', value: '' },
              { label: 'Excess Weight', value: 'excess' },
              { label: 'Disputed', value: 'disputed' },
              { label: 'Resolved', value: 'resolved' },
            ]}
          />

          {/* Bulk Action Selector */}
          {selectedRows.length > 0 && (
            <div className="flex items-center gap-3 ml-auto">
              <div className="text-sm text-text1">
                Selected: {selectedRows.length} items
              </div>
              <Select
                className="w-40"
                value={bulkAction}
                onChange={(e) =>
                  setBulkAction(e.target.value as 'accept' | 'raise' | '')
                }
                placeholder="Bulk Action"
                options={[
                  { label: 'Bulk Action', value: '' },
                  { label: 'Accept', value: 'accept' },
                  { label: 'Raise Dispute', value: 'raise' },
                ]}
              />
              <button
                onClick={handleBulkAction}
                disabled={!bulkAction}
                className={`px-4 py-2 rounded-lg font-medium ${
                  bulkAction
                    ? 'bg-teal text-white hover:bg-teal-dark cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {[
            {
              key: 'new',
              label: 'New Discrepancy',
              count: DISCREPANCY_DATA.filter((d) => d.status === 'new').length,
            },
            {
              key: 'accepted',
              label: 'Discrepancy Accepted',
              count: DISCREPANCY_DATA.filter((d) => d.status === 'accepted')
                .length,
            },
            {
              key: 'raised',
              label: 'Raised',
              count: DISCREPANCY_DATA.filter((d) => d.status === 'raised')
                .length,
            },
            {
              key: 'all',
              label: 'All Discrepancy',
              count: DISCREPANCY_DATA.length,
            },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`px-6 py-3 font-semibold text-base relative ${
                activeTab === tab.key
                  ? 'text-teal border-b-2 border-teal'
                  : 'text-text1 hover:text-text'
              }`}
              onClick={() => {
                setActiveTab(tab.key as any);
                setSelectedRows([]); // Clear selection when changing tabs
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          <div className="space-y-3">
            {/* Detached Teal Table Header Card */}
            <div className="bg-teal border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
              <div className={`grid gap-2 xl:gap-3 items-center ${GRID_COLS}`}>
                {/* Select All Checkbox */}
                <div className="flex items-center">
                  <Checkbox
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="mr-2 border-white data-[state=checked]:bg-white data-[state=checked]:text-teal"
                  />
                  <span>Select</span>
                </div>
                <div>Date</div>
                {admin && <div>CRN</div>}
                <div>Product Detail</div>
                <div>Shipping Details</div>
                <div>Mentioned Weight</div>
                <div>Charged Weight</div>
                <div>Excess Weight & Charge</div>
                <div>Courier Image</div>
                <div>Status</div>
                <div className="text-center">Action</div>
              </div>
            </div>

            {/* Table Rows */}
            {filteredData.length === 0 ? (
              <div className="bg-white border rounded-xl py-20 text-center text-text1">
                No weight discrepancies found
              </div>
            ) : (
              filteredData.map((row) => (
                <div
                  key={row.id}
                  className="bg-card border rounded-xl px-3 xl:px-5 py-3 shadow-sm transition-hover hover:shadow-md"
                >
                  <div
                    className={`grid gap-2 xl:gap-3 text-xs xl:text-sm text-gray-700 items-start ${GRID_COLS}`}
                  >
                    {/* Select Checkbox */}
                    <div className="pt-1">
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onChange={(e) =>
                          handleSelectRow(row.id, e.target.checked)
                        }
                      />
                    </div>

                    {/* Date */}
                    <div className="font-medium text-text">{row.date}</div>

                    {/* CRN (Admin Only) */}
                    {admin && (
                      <div className="font-medium text-blue-500 underline">
                        {row.crn}
                      </div>
                    )}

                    {/* Product Detail */}
                    <div className="space-y-1">
                      <div className="font-bold text-text line-clamp-1">
                        {row.productName}
                      </div>
                      <div className="text-sm text-text1">
                        ID: {row.orderId}
                      </div>
                      <div className="text-sm text-text1">SKU: {row.sku}</div>
                      <div className="text-sm text-text1">Qty: {row.qty}</div>
                    </div>

                    {/* Shipping Details */}
                    <div className="space-y-1">
                      <div className="font-bold text-blue-500 underline">
                        {row.awb}
                      </div>
                      <div className="text-sm font-medium text-text3">
                        {row.courier}
                      </div>
                    </div>

                    {/* Mentioned Weight */}
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-text3">Dead:</span>{' '}
                        {row.mentionedDeadWeight}kg
                      </div>
                      <div>
                        <span className="text-text3">Vol:</span>{' '}
                        {row.mentionedVolumetricWeight}kg
                      </div>
                      <div className="font-bold text-teal">
                        {row.mentionedSlab}
                      </div>
                    </div>

                    {/* Charged Weight */}
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-text3">Dead:</span>{' '}
                        {row.chargedDeadWeight}kg
                      </div>
                      <div>
                        <span className="text-text3">Vol:</span>{' '}
                        {row.chargedVolumetricWeight}kg
                      </div>
                      <div className="font-bold text-red-500">
                        {row.chargedSlab}
                      </div>
                    </div>

                    {/* Excess Weight & Charge */}
                    <div className="space-y-1">
                      <div className="font-bold text-text">
                        +{row.excessWeight} Kg
                      </div>
                      <div className="font-bold text-red-600">
                        ₹{row.excessCharges.toLocaleString('en-IN')}
                      </div>
                    </div>

                    {/* Courier Image */}
                    <div className="flex justify-start">
                      <button className="flex items-center gap-1.5 text-teal font-bold text-sm hover:underline  tracking-wider">
                        <Eye size={14} /> Open Image
                      </button>
                    </div>

                    {/* Status */}
                    <div>
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold  tracking-wider ${getStatusColor(
                          row.status
                        )}`}
                      >
                        {getStatusIcon(row.status)}
                        {row.status}
                      </span>
                    </div>

                    {/* Action */}
                    <div className="flex flex-col gap-2">
                      {row.status === 'new' && (
                        <>
                          <button
                            onClick={() => handleRaiseDispute(row)}
                            className="w-full py-1.5 bg-teal/10 text-teal border border-teal/20 rounded-md text-sm font-bold hover:bg-teal hover:text-white transition-all"
                          >
                            RAISE DISPUTE
                          </button>
                          <button
                            onClick={() => handleAcceptDiscrepancy(row.id)}
                            className="w-full py-1.5 bg-teal text-white rounded-md text-sm font-bold hover:bg-teal-dark transition-all"
                          >
                            ACCEPT
                          </button>
                        </>
                      )}
                      {row.hasHistory && (
                        <button
                          onClick={() =>
                            setExpandedRow(
                              expandedRow === row.id ? null : row.id
                            )
                          }
                          className="flex items-center justify-center gap-1 text-teal font-bold text-[10px]  mt-1"
                        >
                          {expandedRow === row.id ? 'Hide' : 'View'} History
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded History logic inside the card */}
                  {expandedRow === row.id && row.hasHistory && (
                    <div className="mt-4 pt-4 border-t border-dashed">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-bold text-text  mb-3 flex items-center gap-2">
                          <Clock size={14} /> Dispute Timeline
                        </h4>
                        <div className="space-y-3">
                          {row.remarks.map((remark, idx) => (
                            <div key={idx} className="flex gap-4 text-sm">
                              <div className="text-text3 min-w-[80px]">
                                {remark.date}
                              </div>
                              <div className="font-bold text-teal min-w-[100px]">
                                {remark.remarkFrom}
                              </div>
                              <div className="text-text italic">
                                "{remark.remark}"
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Footer Tags */}
                  <hr className="my-4 border-gray-100" />
                  <div className="flex justify-between items-center">
                    <div className="text-left">
                      <span className="inline-block bg-gray-100 text-gray-600 text-sm px-3 py-1 rounded-full">
                        Tags: Weight Discrepancy, {row.courier},{' '}
                        {row.status === 'new' ? 'Action Required' : 'Processed'}
                      </span>
                    </div>
                    <div className="text-sm font-bold text-teal  tracking-widest">
                      AWB: {row.awb}
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Bottom Padding */}
            <div className="h-20" />
          </div>
        </div>

        {/* Modal Component */}
        <Modal />
      </div>
    </div>
  );
}
